import { c as _c } from "react/compiler-runtime";
import type { IconType } from '@/app/components/icons';
import { Icon } from '@/app/components/icons';
import type { Theme } from '@/app/types/styles/theme';
import { cn } from '@/app/utils/cn';
import { cnva } from '@/app/utils/cnva';
import { createDataAttributes } from '@/app/utils/createDataAttributes';
export interface BadgeItem {
  id: string;
  label: string;
  iconBefore?: IconType;
  iconAfter?: IconType;
}
export interface BadgeItemProps extends BadgeItem {
  theme?: Theme;
  badgeClassName?: string;
  iconClassName?: string;
  onClick?: () => void;
}
export interface BadgesProps {
  badges: BadgeItem[];
  theme?: Theme;
  className?: string;
  badgeClassName?: string;
  iconClassName?: string;
}
const BadgeStyled = cnva('font-noto-bold text-button-1 rounded-2xs before:rounded-2xs relative inline-flex w-max items-center justify-center gap-x-2 px-3 py-1 text-center text-white before:absolute before:inset-0 before:border before:border-white', {
  variants: {
    theme: {
      light: 'bg-white text-gray-900 before:border-gray-800',
      dark: 'bg-gray-900 text-white before:border-white'
    }
  },
  defaultVariants: {
    theme: 'light'
  }
});
const BadgeIconStyled = cnva('size-5 flex-none', {
  variants: {
    theme: {
      light: 'text-gray-800',
      dark: 'text-white'
    }
  },
  defaultVariants: {
    theme: 'light'
  }
});
const BadgeItem = t0 => {
  const $ = _c(28);
  const {
    id,
    label,
    theme: t1,
    iconBefore,
    iconAfter,
    badgeClassName,
    iconClassName,
    onClick
  } = t0;
  const theme = t1 === undefined ? "light" : t1;
  const t2 = badgeClassName ? badgeClassName : undefined;
  let t3;
  if ($[0] !== t2 || $[1] !== theme) {
    t3 = BadgeStyled({
      theme,
      className: t2
    });
    $[0] = t2;
    $[1] = theme;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const badgeStyled = t3;
  const t4 = iconClassName ? undefined : theme;
  const t5 = iconClassName ? iconClassName : undefined;
  let t6;
  if ($[3] !== t4 || $[4] !== t5) {
    t6 = BadgeIconStyled({
      theme: t4,
      className: t5
    });
    $[3] = t4;
    $[4] = t5;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  const iconStyled = t6;
  let t7;
  if ($[6] !== iconStyled) {
    t7 = icon => <Icon icon={icon} className={iconStyled} />;
    $[6] = iconStyled;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  const renderIcon = t7;
  let t8;
  if ($[8] !== iconBefore || $[9] !== renderIcon) {
    t8 = !!iconBefore && renderIcon(iconBefore);
    $[8] = iconBefore;
    $[9] = renderIcon;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== label) {
    t9 = <span className="truncate leading-6">{label}</span>;
    $[11] = label;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  let t10;
  if ($[13] !== iconAfter || $[14] !== renderIcon) {
    t10 = !!iconAfter && renderIcon(iconAfter);
    $[13] = iconAfter;
    $[14] = renderIcon;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  let t11;
  if ($[16] !== t10 || $[17] !== t8 || $[18] !== t9) {
    t11 = <>{t8}{t9}{t10}</>;
    $[16] = t10;
    $[17] = t8;
    $[18] = t9;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  const content = t11;
  let t12;
  if ($[20] !== id || $[21] !== label) {
    t12 = createDataAttributes({
      test: id,
      "ga-text": label
    });
    $[20] = id;
    $[21] = label;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] !== badgeStyled || $[24] !== content || $[25] !== onClick || $[26] !== t12) {
    t13 = <div onClick={onClick} className={badgeStyled} {...t12}>{content}</div>;
    $[23] = badgeStyled;
    $[24] = content;
    $[25] = onClick;
    $[26] = t12;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  return t13;
};
const Badges = t0 => {
  const $ = _c(17);
  const {
    badges: t1,
    theme: t2,
    className,
    badgeClassName,
    iconClassName
  } = t0;
  let t3;
  if ($[0] !== t1) {
    t3 = t1 === undefined ? [] : t1;
    $[0] = t1;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const badges = t3;
  const theme = t2 === undefined ? "light" : t2;
  let t4;
  if ($[2] !== className) {
    t4 = cn("pointer-events-none flex flex-wrap gap-4", className);
    $[2] = className;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = createDataAttributes({
      test: "tag-group"
    });
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] !== badgeClassName || $[6] !== badges || $[7] !== iconClassName || $[8] !== theme) {
    let t7;
    if ($[10] !== badgeClassName || $[11] !== iconClassName || $[12] !== theme) {
      t7 = tag => <BadgeItem key={tag.id} id={tag.id} label={tag.label} theme={theme} iconBefore={tag.iconBefore} iconAfter={tag.iconAfter} badgeClassName={badgeClassName} iconClassName={iconClassName} />;
      $[10] = badgeClassName;
      $[11] = iconClassName;
      $[12] = theme;
      $[13] = t7;
    } else {
      t7 = $[13];
    }
    t6 = badges.map(t7);
    $[5] = badgeClassName;
    $[6] = badges;
    $[7] = iconClassName;
    $[8] = theme;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[14] !== t4 || $[15] !== t6) {
    t7 = <div className={t4} role="group" {...t5}>{t6}</div>;
    $[14] = t4;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  return t7;
};
export { BadgeItem, Badges };